import type {
  CommitmentStateT,
  DaypartT,
  EnergyT,
  PriorityT,
  Recurrence,
  Task,
  TaskActionabilityT,
  TaskWorkflowStateT,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import {
  type WithNow,
  getDeletedMap,
  getInitiativesMap,
  getKeyResultsMap,
  getObjectivesMap,
  getTasksMap,
} from './doc.ts';

/**
 * Task write path. Every mutable field is stored as a key on the inner Y.Map
 * so two concurrent edits to *different* fields merge without conflict.
 * The `tags` field is stored as a plain array (replaced atomically on update)
 * because we don't yet need partial-merge semantics across devices for tags.
 * If that changes, switch tags to a Y.Array without breaking readers.
 */

export interface CreateTaskInput {
  title: string;
  projectId: string;
  parentTaskId?: string;
  notes?: string;
  krId?: string;
  objectiveId?: string;
  initiativeId?: string;
  planningContextIds?: string[];
  workflowState?: TaskWorkflowStateT;
  commitmentState?: CommitmentStateT;
  actionability?: TaskActionabilityT;
  expectedOutcome?: string;
  availabilityTrigger?: string;
  waitingReason?: string;
  reviewAt?: number;
  priority?: PriorityT;
  dueAt?: number;
  dueAtHasTime?: boolean;
  scheduledAt?: number;
  scheduledAtHasTime?: boolean;
  reminderAt?: number;
  estimateMinutes?: number;
  energy?: EnergyT;
  daypart?: DaypartT;
  location?: string;
  owner?: string;
  url?: string;
  postponed?: number;
  source?: string;
  favorite?: boolean;
  tags?: string[];
  dependsOn?: string[];
  recurrence?: Recurrence;
  trashedAt?: number;
  manualOrder?: number;
}

/**
 * Patch shape for updateTask. `undefined` explicitly clears the field;
 * absent keys leave the field unchanged.
 */
export type UpdateTaskInput = {
  [K in keyof Omit<CreateTaskInput, 'projectId'>]?: CreateTaskInput[K] | undefined;
} & {
  projectId?: string;
};

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

export function getTask(doc: Y.Doc, id: string): Task | undefined {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return undefined;
  return taskFromMap(id, inner);
}

export function listTasks(doc: Y.Doc): Task[] {
  const tasks: Task[] = [];
  for (const [id, inner] of getTasksMap(doc).entries()) {
    tasks.push(taskFromMap(id, inner));
  }
  return tasks;
}

export function createTask(doc: Y.Doc, input: CreateTaskInput, options: WithNow = {}): Task {
  validateTaskRelationships(doc, input);
  const now = ts(options);
  const id = newId();
  const inner = new Y.Map<unknown>();

  inner.set('title', input.title);
  inner.set('projectId', input.projectId);
  inner.set('priority', input.priority ?? 0);
  inner.set('workflowState', input.workflowState ?? 'backlog');
  inner.set('tags', [...(input.tags ?? [])]);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);

  setOptional(inner, 'notes', input.notes);
  setOptional(inner, 'parentTaskId', input.parentTaskId);
  setOptional(inner, 'krId', input.krId);
  setOptional(inner, 'objectiveId', input.objectiveId);
  setOptional(inner, 'initiativeId', input.initiativeId);
  setOptional(
    inner,
    'planningContextIds',
    input.planningContextIds ? [...input.planningContextIds] : undefined,
  );
  setOptional(inner, 'commitmentState', input.commitmentState);
  setOptional(inner, 'actionability', input.actionability);
  setOptional(inner, 'expectedOutcome', input.expectedOutcome);
  setOptional(inner, 'availabilityTrigger', input.availabilityTrigger);
  setOptional(inner, 'waitingReason', input.waitingReason);
  setOptional(inner, 'reviewAt', input.reviewAt);
  setOptional(inner, 'dueAt', input.dueAt);
  setOptional(
    inner,
    'dueAtHasTime',
    input.dueAt === undefined ? undefined : (input.dueAtHasTime ?? true),
  );
  setOptional(inner, 'scheduledAt', input.scheduledAt);
  setOptional(
    inner,
    'scheduledAtHasTime',
    input.scheduledAt === undefined ? undefined : (input.scheduledAtHasTime ?? true),
  );
  setOptional(inner, 'reminderAt', input.reminderAt);
  setOptional(inner, 'estimateMinutes', input.estimateMinutes);
  setOptional(inner, 'energy', input.energy);
  setOptional(inner, 'daypart', input.daypart);
  setOptional(inner, 'location', input.location);
  setOptional(inner, 'owner', input.owner);
  setOptional(inner, 'url', input.url);
  setOptional(inner, 'postponed', input.postponed);
  setOptional(inner, 'source', input.source);
  setOptional(inner, 'favorite', input.favorite);
  setOptional(inner, 'dependsOn', input.dependsOn ? [...input.dependsOn] : undefined);
  setOptional(
    inner,
    'recurrence',
    input.recurrence
      ? { ...input.recurrence, seriesId: input.recurrence.seriesId ?? id }
      : undefined,
  );
  setOptional(inner, 'trashedAt', input.trashedAt);
  setOptional(inner, 'manualOrder', input.manualOrder);

  getTasksMap(doc).set(id, inner);
  return taskFromMap(id, inner);
}

export function updateTask(
  doc: Y.Doc,
  id: string,
  patch: UpdateTaskInput,
  options: WithNow = {},
): void {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  validateTaskRelationships(doc, {
    ...taskFromMap(id, inner),
    ...patch,
    projectId: patch.projectId ?? String(inner.get('projectId') ?? ''),
  });

  doc.transact(() => {
    for (const [key, value] of Object.entries(patch) as [keyof UpdateTaskInput, unknown][]) {
      if (key === 'tags') {
        inner.set('tags', Array.isArray(value) ? [...value] : []);
        continue;
      }
      if (key === 'dependsOn') {
        if (value === undefined) inner.delete('dependsOn');
        else inner.set('dependsOn', Array.isArray(value) ? [...value] : []);
        continue;
      }
      if (key === 'planningContextIds') {
        if (value === undefined) inner.delete('planningContextIds');
        else inner.set('planningContextIds', Array.isArray(value) ? [...value] : []);
        continue;
      }
      if (key === 'recurrence') {
        if (value === undefined) inner.delete('recurrence');
        else inner.set('recurrence', { ...(value as Recurrence) });
        continue;
      }
      if (key === 'dueAt' && value === undefined) {
        inner.delete('dueAt');
        inner.delete('dueAtHasTime');
        continue;
      }
      if (key === 'scheduledAt' && value === undefined) {
        inner.delete('scheduledAt');
        inner.delete('scheduledAtHasTime');
        continue;
      }
      if (key === 'dueAt' && !Object.prototype.hasOwnProperty.call(patch, 'dueAtHasTime')) {
        inner.set('dueAt', value);
        inner.set('dueAtHasTime', true);
        continue;
      }
      if (
        key === 'scheduledAt' &&
        !Object.prototype.hasOwnProperty.call(patch, 'scheduledAtHasTime')
      ) {
        inner.set('scheduledAt', value);
        inner.set('scheduledAtHasTime', true);
        continue;
      }
      if (value === undefined) {
        inner.delete(key);
      } else {
        inner.set(key, value);
      }
    }
    inner.set('updatedAt', now);
  });
}

export function completeTask(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    inner.set('completedAt', now);
    inner.set('updatedAt', now);
  });
}

export function reopenTask(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    inner.delete('completedAt');
    inner.set('updatedAt', now);
  });
}

export function deleteTask(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const tasks = getTasksMap(doc);
  if (!tasks.has(id)) return;
  const now = ts(options);
  doc.transact(() => {
    tasks.delete(id);
    getDeletedMap(doc).set(id, now);
  });
}

export function trashTask(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    inner.set('trashedAt', now);
    inner.set('updatedAt', now);
  });
}

export function restoreTask(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const inner = getTasksMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    inner.delete('trashedAt');
    inner.set('updatedAt', now);
  });
}

export function reorderTasks(doc: Y.Doc, orderedVisibleIds: string[]): void {
  const visibleIds = [...new Set(orderedVisibleIds.filter((id) => getTasksMap(doc).has(id)))];
  if (visibleIds.length === 0) return;

  const visibleIdSet = new Set(visibleIds);
  const allTaskIds = listTasks(doc)
    .sort(compareManualTaskOrder)
    .map((task) => task.id);
  let visibleIndex = 0;
  const mergedOrder = allTaskIds.map((id) =>
    visibleIdSet.has(id) ? (visibleIds[visibleIndex++] ?? id) : id,
  );

  doc.transact(() => {
    mergedOrder.forEach((id, index) => {
      const inner = getTasksMap(doc).get(id);
      if (inner && inner.get('manualOrder') !== index) inner.set('manualOrder', index);
    });
  });
}

// ─── Internals ────────────────────────────────────────────────────────────

function setOptional(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value === undefined) return;
  inner.set(key, value);
}

function taskFromMap(id: string, inner: Y.Map<unknown>): Task {
  const rawTags = inner.get('tags');
  const task: Task = {
    id,
    title: String(inner.get('title') ?? ''),
    projectId: String(inner.get('projectId') ?? ''),
    workflowState: (inner.get('workflowState') ?? 'backlog') as TaskWorkflowStateT,
    priority: (inner.get('priority') ?? 0) as PriorityT,
    tags: Array.isArray(rawTags) ? (rawTags as string[]).slice() : [],
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };

  copyOptionalString(inner, task, 'notes');
  copyOptionalString(inner, task, 'parentTaskId');
  copyOptionalString(inner, task, 'krId');
  copyOptionalString(inner, task, 'objectiveId');
  copyOptionalString(inner, task, 'initiativeId');
  const planningContextIds = inner.get('planningContextIds');
  if (Array.isArray(planningContextIds))
    task.planningContextIds = [...planningContextIds] as string[];
  copyOptional(inner, task, 'commitmentState');
  copyOptional(inner, task, 'actionability');
  copyOptionalString(inner, task, 'expectedOutcome');
  copyOptionalString(inner, task, 'availabilityTrigger');
  copyOptionalString(inner, task, 'waitingReason');
  copyOptionalNumber(inner, task, 'reviewAt');
  copyOptionalNumber(inner, task, 'dueAt');
  copyOptionalBoolean(inner, task, 'dueAtHasTime');
  copyOptionalNumber(inner, task, 'scheduledAt');
  copyOptionalBoolean(inner, task, 'scheduledAtHasTime');
  copyOptionalNumber(inner, task, 'reminderAt');
  copyOptionalNumber(inner, task, 'estimateMinutes');
  copyOptionalNumber(inner, task, 'completedAt');
  copyOptional(inner, task, 'energy');
  copyOptional(inner, task, 'daypart');
  copyOptionalString(inner, task, 'location');
  copyOptionalString(inner, task, 'owner');
  copyOptionalString(inner, task, 'url');
  copyOptionalNumber(inner, task, 'postponed');
  copyOptionalString(inner, task, 'source');
  copyOptionalBoolean(inner, task, 'favorite');
  copyOptionalNumber(inner, task, 'trashedAt');
  copyOptionalNumber(inner, task, 'manualOrder');

  const dependsOn = inner.get('dependsOn');
  if (Array.isArray(dependsOn)) task.dependsOn = (dependsOn as string[]).slice();

  const recurrence = inner.get('recurrence');
  if (recurrence && typeof recurrence === 'object') {
    task.recurrence = { ...(recurrence as Recurrence) };
  }

  return task;
}

function compareManualTaskOrder(left: Task, right: Task): number {
  const leftOrder = left.manualOrder ?? Number.MAX_SAFE_INTEGER;
  const rightOrder = right.manualOrder ?? Number.MAX_SAFE_INTEGER;
  return (
    leftOrder - rightOrder || left.createdAt - right.createdAt || left.id.localeCompare(right.id)
  );
}

function copyOptional<K extends keyof Task>(inner: Y.Map<unknown>, task: Task, key: K): void {
  const value = inner.get(key as string);
  if (value !== undefined) (task as Record<string, unknown>)[key as string] = value;
}

function copyOptionalString<K extends keyof Task>(inner: Y.Map<unknown>, task: Task, key: K): void {
  const value = inner.get(key as string);
  if (typeof value === 'string') (task as Record<string, unknown>)[key as string] = value;
}

function copyOptionalNumber<K extends keyof Task>(inner: Y.Map<unknown>, task: Task, key: K): void {
  const value = inner.get(key as string);
  if (typeof value === 'number') (task as Record<string, unknown>)[key as string] = value;
}

function copyOptionalBoolean<K extends keyof Task>(
  inner: Y.Map<unknown>,
  task: Task,
  key: K,
): void {
  const value = inner.get(key as string);
  if (typeof value === 'boolean') (task as Record<string, unknown>)[key as string] = value;
}

function validateTaskRelationships(
  doc: Y.Doc,
  input: {
    projectId: string;
    krId?: string | undefined;
    objectiveId?: string | undefined;
    initiativeId?: string | undefined;
  },
): void {
  let objectiveId = input.objectiveId;
  if (input.krId !== undefined) {
    const keyResult = getKeyResultsMap(doc).get(input.krId);
    if (!keyResult) throw new Error(`key result ${input.krId} not found`);
    const krObjectiveId = String(keyResult.get('objectiveId') ?? '');
    if (objectiveId !== undefined && objectiveId !== krObjectiveId) {
      throw new Error('task objectiveId conflicts with key result objective');
    }
    objectiveId = krObjectiveId;
  }
  if (objectiveId !== undefined) {
    const objective = getObjectivesMap(doc).get(objectiveId);
    if (!objective) throw new Error(`objective ${objectiveId} not found`);
    if (objective.get('projectId') !== input.projectId) {
      throw new Error('task objective must belong to the same project');
    }
  }
  if (input.initiativeId !== undefined) {
    const initiative = getInitiativesMap(doc).get(input.initiativeId);
    if (!initiative) throw new Error(`initiative ${input.initiativeId} not found`);
    if (initiative.get('projectId') !== input.projectId) {
      throw new Error('task initiative must belong to the same project');
    }
    const objectiveIds = initiative.get('objectiveIds');
    if (
      objectiveId !== undefined &&
      (!Array.isArray(objectiveIds) || !(objectiveIds as string[]).includes(objectiveId))
    ) {
      throw new Error('task objective must be supported by its initiative');
    }
  }
}
